import { Node, mergeAttributes } from '@tiptap/core';

export const MergeTagNode = Node.create({
  name: 'mergeTag',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      tag: { default: '' },
      label: { default: '' },
      // Editor-only flag. When `true`, the pill renders as plain inline
      // text (no green background) — used to preview what the document
      // will look like once the merge tag is substituted with a real
      // value (e.g. mediator picked → "{{mediator_name}}" pill displays
      // "Testing Qa Account" without the placeholder pill chrome).
      // The saved content is unaffected: editorContentToHtml replaces
      // the span back to its raw {{tag}} regardless of resolved state.
      resolved: { default: false },
    };
  },

  parseHTML() {
    // Extract the data-merge-tag + label attributes back into node.attrs
    // when re-loading saved HTML. Without this, a round-trip
    // (load → render → save) drops the tag attribute and we end up with
    // `<span data-merge-tag="" tag="">label</span>` in storage — which
    // editorContentToHtml's regex can't match, so the {{tag}} substitution
    // on the BE side fails and the agreement renders raw labels.
    return [
      {
        tag: 'span[data-merge-tag]',
        getAttrs: (node) => ({
          tag:
            node.getAttribute('data-merge-tag') ||
            node.getAttribute('tag') ||
            '',
          label:
            node.getAttribute('label') ||
            node.textContent ||
            '',
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-merge-tag': HTMLAttributes.tag,
        style:
          'display:inline-block;background:#d1fae5;color:#065f46;padding:2px 8px;border-radius:4px;font-weight:500;font-size:13px;cursor:default;user-select:all;',
      }),
      HTMLAttributes.label || HTMLAttributes.tag,
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('span');
      dom.dataset.mergeTag = node.attrs.tag;
      dom.textContent = node.attrs.label || node.attrs.tag;
      if (node.attrs.resolved) {
        // Resolved → render as plain inline text. No background, no
        // padding, inherit surrounding font weight + size. Looks like
        // a normal piece of body text.
        Object.assign(dom.style, {
          display: 'inline',
          background: 'transparent',
          color: 'inherit',
          padding: '0',
          borderRadius: '0',
          fontWeight: 'inherit',
          fontSize: 'inherit',
          cursor: 'default',
          userSelect: 'all',
        });
      } else {
        // Unresolved (default) → green pill chrome so the CM can see
        // exactly where merge tags live in the template.
        Object.assign(dom.style, {
          display: 'inline-block',
          background: '#d1fae5',
          color: '#065f46',
          padding: '2px 8px',
          borderRadius: '4px',
          fontWeight: '500',
          fontSize: '13px',
          cursor: 'default',
          userSelect: 'all',
        });
      }
      return { dom };
    };
  },
});

// Convert raw {{tag}} text to mergeTag nodes in HTML
export function htmlToEditorContent(html, mergeTags) {
  if (!html) return '';
  let result = html;
  for (const mt of mergeTags) {
    const escaped = mt.tag.replace(/[{}]/g, (c) => `&#${c.charCodeAt(0)};`);
    result = result.replaceAll(
      mt.tag,
      `<span data-merge-tag="${mt.tag}" label="${mt.label}">${mt.label}</span>`
    );
  }
  return result;
}

// Convert mergeTag nodes back to raw {{tag}} for saving
export function editorContentToHtml(html, mergeTags) {
  if (!html) return '';
  let result = html;
  // Replace span[data-merge-tag] with the raw tag
  for (const mt of mergeTags) {
    const regex = new RegExp(
      `<span[^>]*data-merge-tag="${mt.tag.replace(/[{}]/g, '\\$&')}"[^>]*>[^<]*</span>`,
      'g'
    );
    result = result.replace(regex, mt.tag);
  }
  return result;
}
